'use client';

import { ClockUserIcon } from "@phosphor-icons/react";
import React, { useContext, useEffect, useState, useCallback } from 'react';
import Meter from '@/components/ui/meter';
import { MeteringFromTable } from '@/app/api/protected/metering/route';
import { FetchWithVersionCheck, CaptureError } from '@/shared/functions';
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
import { AuthContext, FeaturesContext } from '@/app/contexts';
import { formatTime } from '@/shared/clientFunctions';
import Backlog, { BacklogFromApi } from "./ui/backlog";
import { recordStat } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import { InternalTools } from './internal-tools';
import { SwitchTenant } from './switch-tenant';
import { RemoteStorage } from "@/shared/types";
export interface StatusBarProps {
  status: string;
  userId: string;
  timeSaved: number;
  loadingTimeSaved: boolean;
  caseId: string | undefined;
  remoteStorageLink: RemoteStorage | null;
}
const StatusBar: React.FC<StatusBarProps> = ({
  status,
  userId,
  timeSaved,
  loadingTimeSaved,
  caseId,
  remoteStorageLink
}) => {
  const [metering, setMetering] = useState<MeteringFromTable | null>(null);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [backlogData, setBacklogData] = useState<BacklogFromApi>();
  const [provenanceStatusCounts, setProvenanceStatusCounts] = useState<{
    [key: string]: number;
  }>({});
  const [timeSavedLocal, setTimeSavedLocal] = useState(timeSaved);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const statsContext = useContext(StatsContext);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  useEffect(() => {
    setTimeSavedLocal(timeSaved);
  }, [timeSaved]);
  const fetchMetering = useCallback(async () => {
    try {
      const response = await FetchWithVersionCheck('/api/protected/metering', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken || ''
        }
      });
      const data = await response.json();
      // if quotaInBytes is 0, then we don't have a cap to show the % meter
      if (data && data.usageInBytes && data.quotaInBytes) {
        setMetering({
          usageInBytes: data.usageInBytes || 0,
          quotaInBytes: data.quotaInBytes || 0
        });
      }
      if (!response.ok || !data) {
        console.log("Metering unavailable - response:", response);
      }
    } catch (error) {
      const errorMessage = `Error fetching metering info: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  }, [idToken, accessToken, featureFlags?.useIntercom]);
  useEffect(() => {
    if (featureFlags?.metering && idToken && accessToken) {
      fetchMetering();
    }
  }, [fetchMetering, featureFlags?.metering, idToken, accessToken]);
  const fetchBacklog = useCallback(async () => {
    try {
      const response = await FetchWithVersionCheck('/api/protected/backlog', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken || ''
        }
      });
      const data = await response.json();
      if (!response.ok || !data) {
        const errorMessage = `Error fetching sqs/backlog info`;
        CaptureError(errorMessage, undefined, !!featureFlags?.useIntercom);
      }
      if (data && data.backlog) {
        setBacklogData(data.backlog);
      }
    } catch (error) {
      const errorMessage = `Error fetching sqs/backlog info: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  }, [idToken, accessToken, featureFlags?.useIntercom]);
  useEffect(() => {
    if (featureFlags?.backlog && idToken && accessToken) {
      fetchBacklog();
    }
  }, [fetchBacklog, featureFlags?.backlog, idToken, accessToken]);
  const fetchProvenaceData = useCallback(async () => {
    try {
      const response = await FetchWithVersionCheck('/api/protected/provenance-service-proxy/status_counts', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data) {
        const errorMessage = `Error fetching provenance service status counts`;
        CaptureError(errorMessage, undefined, !!featureFlags?.useIntercom);
        return;
      }
      if (!!data.responses?.[0]?.counts) {
        setProvenanceStatusCounts(data.responses[0].counts as {
          [key: string]: number;
        });
      }
    } catch (error) {
      const errorMessage = `Error fetching provenance service status counts: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
  }, [featureFlags?.useIntercom]);
  useEffect(() => {
    if (featureFlags?.backlog) {
      fetchProvenaceData();
    }
  }, [fetchProvenaceData, featureFlags?.backlog]);
  const isFooter = !!featureFlags?.enableMediaAndSourceShare;
  return <div className={`box-border w-full hidden sm:flex fixed bg-[#060F1B] flex-row items-center py-1 px-3 sm:px-4 gap-[15px] text-center text-3xs text-gray-100 font-inter min-h-[39px] ${isFooter ? 'bottom-0 left-0 right-0 justify-start border-t border-white/[0.06] z-40' : 'top-[72px] left-0 justify-center border-b border-white/[0.06]'}`} data-sentry-component="StatusBar" data-sentry-source-file="status-bar.tsx">
      {(!!featureFlags?.['timeSaved'] || !!featureFlags?.['newTimeSaved']) && <div className="flex flex-row gap-1.5 items-center justify-center text-sm opacity-60 hover:opacity-100 whitespace-nowrap">
          <ClockUserIcon size={22} weight="duotone" />
        {loadingTimeSaved ? <div className="animate-pulse">Calculating time savings with TimePilot...</div> : <Popover data-testid="TimeSavingsPopoverRead" onOpenChange={open => {
        setIsPopoverVisible(open);
        if (open) {
          recordStat('TimeSavingsPopoverReadHover', new MouseEvent('hover') as unknown as React.MouseEvent, statsContext, !!featureFlags?.useIntercom);
        }
      }} placement={isFooter ? "top-start" : "bottom-start"} showArrow={true} triggerScaleOnOpen={false} classNames={{
        content: 'flex flex-col justify-start items-start bg-white text-black max-w-[350px] p-4 rounded-md'
      }}>
            <PopoverTrigger>
              <button type="button" data-testid="TimeSavingsPopover" aria-label={`Time Savings: ${formatTime(timeSavedLocal, true)} (${formatTime(timeSavedLocal, false, true)})`} aria-haspopup="dialog" className="flex flex-row items-center justify-center gap-1 hover:cursor-help bg-transparent border-none p-0" onMouseEnter={event => recordStat('TimeSavingsPopoverClicked', event, statsContext, !!featureFlags?.useIntercom)}>
                Time Savings: <span className="italic text-[15px] pr-[3px] pt-[.5px] opacity-100">{formatTime(timeSavedLocal, true)} ({formatTime(timeSavedLocal, false, true)})</span>
              </button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="flex flex-col gap-4">
                <div className="flex flex-row gap-4">
                  <div className="">
                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="#2d8dfe" viewBox="0 0 256 256" stroke="#2d8dfe">
                      <path d="M136,72v43.05l36.42-18.21a8,8,0,0,1,7.16,14.31l-48,24A8,8,0,0,1,120,128V72a8,8,0,0,1,16,0Zm-8,144a88,88,0,1,1,88-88,8,8,0,0,0,16,0A104,104,0,1,0,128,232a8,8,0,0,0,0-16Zm103.73,5.94a8,8,0,1,1-15.46,4.11C213.44,215.42,203.46,208,192,208s-21.44,7.42-24.27,18.05A8,8,0,0,1,160,232a8.15,8.15,0,0,1-2.06-.27,8,8,0,0,1-5.67-9.79,40,40,0,0,1,17.11-23.32,32,32,0,1,1,45.23,0A40,40,0,0,1,231.73,221.94ZM176,176a16,16,0,1,0,16-16A16,16,0,0,0,176,176Z"></path>
                    </svg>
                  </div>
                  <p className="text-[16px] italic leading-normal text-[#0F1D32] p-0 m-0">
                    Your team has saved <span className="font-semibold text-[18px] text-brand-blue">{formatTime(timeSavedLocal)}</span> investigating dozens of cases with TimePilot.</p>
                </div>
              </div>
            </PopoverContent>
          </Popover>}
      </div>}
      {!isFooter && <div className="flex-1 relative h-[15px] overflow-hidden" />}
      <Backlog backlogData={backlogData as BacklogFromApi} provenanceStatusCounts={provenanceStatusCounts} userId={userId} caseId={caseId} remoteStorageLink={remoteStorageLink} data-sentry-element="Backlog" data-sentry-source-file="status-bar.tsx" />
      {!!featureFlags?.metering && !!metering && !!metering.usageInBytes && !!metering.quotaInBytes && <div className="flex flex-row items-center px-2 h-[30px] max-w-[300px]">
          <Meter overview minimal={true} temperature={true} title="Usage" values={metering} units="Bytes" breakdownLink="https://tranquility-ai.com" breakdownTooltip breakdownLegend />
        </div>}
      {featureFlags?.powerUserOrg && <InternalTools />}
      {featureFlags?.switchTenant && <SwitchTenant />}
    </div>;
};
export default StatusBar;